/* eslint-disable react/prop-types */
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { addRouteNamespace } from '@Utils/actions';
import {
	getSubscriptions,
	PropertyCard,
	useCompanyDateTime,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import './risk-card.css';
import { fullAccess } from '@innovyze/shared-utils';
import { useGlobalization } from '@Translations/useGlobalization';
import { formatDateTimeWithFormatDate } from '@innovyze/lib_am_common/Utils';
import {
	AssetCount,
	AssetCounts,
	RiskConfigCard,
} from '@innovyze/lib_am_common';
import { useDispatch } from 'react-redux';
import {
	changeRiskConfigPublishState,
	clearRiskConfig,
	duplicateRiskConfig,
} from '@Actions/Risk.actions';
import {
	selectRiskConfigsPage,
	selectAssetCounts,
	selectIsAssetCountsReady,
} from '@innovyze/lib_am_common/Selectors';
import { changeRiskConfigsPage } from '@innovyze/lib_am_common/Actions';
import { RiskCalcStatusResult } from '@Types/risk.types';
import { Link, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import HelpIcon from '@mui/icons-material/Help';
import { green, grey } from '@mui/material/colors';
import { ConfigPublishState } from '@Types/riskConfig.types';

interface Props {
	riskConfig: RiskConfigCard;
	onDeleteRisk: (id: string, publishState: ConfigPublishState) => void;
}
const RiskListCard = ({ riskConfig, onDeleteRisk }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);

	const riskNormalizeFlag = useIsFeatureEnabled(
		'info-360-asset-risk-normalize',
	);

	const { formatDate } = useCompanyDateTime();
	const lastRun = formatDateTimeWithFormatDate(
		riskConfig.lastRun,
		formatDate,
	);
	const riskPage = selectRiskConfigsPage();
	const isAssetCountsReady = selectIsAssetCountsReady();
	const assetCounts: AssetCounts = selectAssetCounts();

	const handleViewClick = () => {
		dispatch(clearRiskConfig());
		history.push(addRouteNamespace(`results/${riskConfig.configId}`));
	};

	const handleDeleteClick = () => {
		onDeleteRisk(riskConfig.configId, riskConfig.publishState);
	};

	const onDuplicateComplete = (configId: string) => {
		if (configId) {
			dispatch(clearRiskConfig());
			history.push(addRouteNamespace(`results/${configId}`));

			dispatch(changeRiskConfigsPage(riskPage.page));
		}
	};

	const handlePublishStateChange = (newPublishState: ConfigPublishState) => {
		dispatch(
			changeRiskConfigPublishState({
				newPublishState,
				configId: riskConfig.configId,
				refresh: () => {
					dispatch(changeRiskConfigsPage(riskPage.page));
				},
			}),
		);
	};

	const handleDuplicateClick = () => {
		dispatch(
			duplicateRiskConfig({
				configId: riskConfig.configId,
				onComplete: onDuplicateComplete,
			}),
		);
	};

	const formatRunBy = (runBy: string) => {
		if (runBy.indexOf('@') !== -1) {
			return runBy.substring(0, runBy.lastIndexOf('@'));
		}
		return runBy;
	};

	const menuItems = () => {
		const items = [];

		if (hasFullAccess) {
			if (riskConfig.publishState === ConfigPublishState.published) {
				items.push({
					onClick: () =>
						handlePublishStateChange(
							ConfigPublishState.unpublished,
						),
					text: t('Unpublish'),
				});
			} else {
				items.push({
					onClick: () =>
						handlePublishStateChange(ConfigPublishState.published),
					text: t('Publish'),
				});
			}

			items.push({
				onClick: handleDuplicateClick,
				text: t('Duplicate'),
			});
			items.push({
				onClick: handleDeleteClick,
				text: t('Delete'),
			});
		}

		return items;
	};

	const getRiskStatus = (status: RiskCalcStatusResult) => {
		let result = '-';
		if (status) {
			// DO NOT TRANSLATE THESE... That is done by the status object base on the english string
			switch (status) {
				case 'risk-calc-complete':
					result = 'Complete';
					break;
				case 'risk-calc-cancelled':
					result = 'Cancelled';
					break;
				case 'risk-calc-failed':
				case 'error':
					result = 'Failed';
					break;
				case 'risk-calc-initiated':
				case 'risk-calc-start':
				case 'risk-calc-in-progress':
				case 'risk-calc-getting-assets':
				case 'risk-calc-getting-risk-config':
				case 'risk-calc-getting-inspections':
				case 'risk-calc-archiving-existing-in-snowflake':
				case 'risk-calc-writing-to-snowflake':
				case 'risk-calc-post-processing-normalize':
					result = 'In Progress';
					break;
			}
		}
		return result;
	};

	const getRiskFormula = (riskConfig: RiskConfigCard) => {
		if (
			riskConfig.cofWeighting != undefined &&
			riskConfig.lofWeighting != undefined &&
			riskConfig.riskMethod != undefined
		) {
			return `${riskConfig.lofWeighting}% ${
				riskConfig.riskMethod == 'added' ? '+' : 'x'
			} ${riskConfig.cofWeighting}%`;
		} else {
			return '-';
		}
	};

	const getFormulaValue = (riskConfig: RiskConfigCard) => {
		const formula = getRiskFormula(riskConfig);
		if (riskNormalizeFlag && formula !== '-' && riskConfig.postProcessing) {
			const process = riskConfig.postProcessing.find(
				process => process.type === 'normalize',
			);
			if (process && process.params.useNormalize) {
				const tooltip = `((${formula})/${
					process.params.maxRiskScore ?? t('HCRS')
				}) * ${process.params.normalizeValue ?? t('NV')}`;
				return (
					<div
						title={tooltip}
						style={{
							cursor: 'default',
						}}>
						{t('Normalized')}
						<HelpIcon
							color="secondary"
							style={{
								verticalAlign: 'top',
								height: '20px',
							}}
						/>
					</div>
				);
			}
		}

		return <>{formula}</>;
	};

	const typeCell = (type?: string) => {
		switch (type) {
			case 'wwPipe':
				return t('Sanitary Sewer - Pipe');
			case 'wwManhole':
				return t('Sanitary Sewer - Manhole');
			case 'pipe':
				return t('Water Distibution - Pipe');
			default:
				return '-';
		}
	};

	const renderTitle = useCallback(
		(configName: string, publishState: ConfigPublishState) => {
			if (
				hasFullAccess ||
				publishState === ConfigPublishState.published
			) {
				return (
					<Link
						underline="hover"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							cursor: 'pointer',
							width: '100%',
							whiteSpace: 'nowrap',
						}}
						title={configName}
						onClick={() => {
							handleViewClick();
						}}
						data-cy="card-title">
						{configName}
					</Link>
				);
			} else {
				return (
					<div
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							width: '100%',
							whiteSpace: 'nowrap',
						}}>
						{configName}
					</div>
				);
			}
		},
		[],
	);

	const renderHeader = (
		configName: string,
		configPublishState: ConfigPublishState,
	) => {
		const tooltipText =
			configPublishState === ConfigPublishState.published
				? t('Published')
				: t('Unpublished');

		const icon =
			configPublishState === ConfigPublishState.published ? (
				<CheckCircleIcon sx={{ color: green[500] }} />
			) : (
				<RemoveCircleIcon sx={{ color: grey[500] }} />
			);

		const publishState = <Tooltip title={tooltipText}>{icon}</Tooltip>;

		return (
			<div className="header-container">
				{renderTitle(configName, configPublishState)} {publishState}
			</div>
		);
	};

	return (
		<div
			key={riskConfig.configId}
			style={{
				fontSize: '1rem',
			}}>
			<PropertyCard
				columns={2}
				forceMenu
				className="adjusted-menu-position"
				menuItems={menuItems()}
				properties={[
					{
						title: t('Last Run'),
						value: lastRun.length == 0 ? '-' : lastRun,
						ellipsis: true,
					},
					{
						title: t('Status'),
						type: riskConfig.status ? 'status' : 'text',
						value: getRiskStatus(
							riskConfig.status as RiskCalcStatusResult,
						),
					},
					{
						title: t('Run By'),
						value: riskConfig.runBy
							? formatRunBy(riskConfig.runBy)
							: '-',
						ellipsis: true,
					},
					{
						title:
							riskConfig.riskMethod == 'added'
								? t('Formula (LoF + CoF)')
								: t('Formula (LoF x CoF)'),
						value: getFormulaValue(riskConfig),
						ellipsis: true,
					},
					{
						title: t('System and Asset Type'),
						value: typeCell(riskConfig.assetType),
					},
					{
						title: t('Asset Count'),
						value:
							isAssetCountsReady && assetCounts
								? assetCounts.find((assetCount: AssetCount) => {
										return (
											assetCount.systemType ==
												riskConfig.systemType &&
											assetCount.assetType ==
												riskConfig.assetType
										);
								  })?.count ?? '-'
								: '-',
						// riskConfig.assetCount && riskConfig.assetCount >= 0
						// 	? riskConfig.assetCount
						// 	: '-',
					},
				]}
				title={renderHeader(
					riskConfig.name ?? t('Default Risk Model'),
					riskConfig.publishState,
				)}
			/>
		</div>
	);
};
export default RiskListCard;
